import logging from './logging';

export const log = logging('utils');



/**
 * Removes the item from the array.
 *
 * *Warning* Don't use this on array properties of DomainModel objects. Use the
 * `removed` version to re-assign the whole array property instead of editing
 * its value in place, e.g. item.sections = removed(section, item.sections);
 * @param item the thing to remove
 * @param a the array to remove it from
 * @returns true if it was removed, false otherwise
 */
export const remove = <T>(item: T, a: Array<T>): boolean => {
    const i = a.indexOf(item);
    if(i > -1) {
        a.splice(i, 1);
        return true;
    }
    return false;
}

/**
 * Returns a copy of the array a with the item removed, or merely a duplicate
 * of the array if the item cannot be found.
 * @param item the thing to remove
 * @param a the array to copy; the item will be removed from the copy
 */
export const removed = <T>(item: T, a: T[]): T[] => {
    const b = Array.from(a);
    remove(item, b);
    return b;
}

/**
 * Replace the old item in the array with the given replacement item.
 *
 * *Warning* Don't use this on array properties of DomainModel objects. Use the
 * `replaced` version to re-assign the whole array property instead of editing
 * its value in place, e.g. item.sections = replaced(old, repl, item.sections);
 * @param old the item to be replaced
 * @param replacement the item to replace the old item with
 * @param a the array to operate on
 * @returns true if the item was replaced, false otherwise
 */
export const replace = <T>(old: T, replacement: T, a: Array<T>): boolean => {
    const i = a.indexOf(old);
    if(i > -1) {
        a.splice(i, 1, replacement);
        return true;
    }
    return false;
}

/**
 * Returns a copy of the array a with the old item replaced with the
 * replacement.
 * @param old the item to be replaced
 * @param replacement the item to replace the old item with
 * @param a the array to operate on
 * @returns a copy of a with the old item replaced with the replacement
 */
export const replaced = <T>(old: T, replacement: T, a: T[]): T[] => {
    const b = Array.from(a);
    replace(old, replacement, b);
    return b;
}

/**
 * Returns a new set that contains only elements that appear in both sets. This
 * operation is symmetrical meaning the order of the `a` and `b` parameters
 * should not matter.
 * @returns a new set containing only the items that appear both in a and b.
 */
export const intersection = <T>(a: Set<T>, b: Set<T>): Set<T> => {
    var result = new Set<T>();
    for(let item of a) {
        if(b.has(item)) result.add(item);
    }
    return result;
}

/**
 * Choose the closest numeric element from a list.
 */
export const select = (x: number, input: number[]): number => {
    const differences = new Array(input.length);
    for(let i=0; i<input.length; i++) {
        differences[i] = Math.abs(input[i] - x);
    }
    return input[mini(differences)];
}

// Find the index of the smallest value. Returns -1 for empty arrays.
export const mini = (xs: number[]): number => {
    let m = Number.POSITIVE_INFINITY;
    let mi = -1;
    for(let i = 0; i < xs.length; i++) {
        if(xs[i] < m) {
            m = xs[i];
            mi = i;
        }
    }
    return mi;
}


export const partition = <T>(items: T[], predicate: (T) => boolean): [T[], T[]] =>
{
    let i=0;
    while(i < items.length && predicate(items[i])) {
        i++;
    }
    return [items.slice(0, i), items.slice(i)];
}

/**
 * Iterate through something two at a time. On the last iteration, the second
 * element of the tuple is null because there is no next element.
 */
export function* lookahead<T>(iterable: Iterable<T>): Generator<[T, T?]> {
    let last: T = null;
    for(const item of iterable) {
        if(last == null) {
            last = item;
            continue;
        }
        yield [last, item];
        last = item;
    }
    
    if(last) {
        yield [last, null];
    }
}

export const firstMatching = <T>(iterable: Iterable<T>, p: (t: T) => boolean)
    : T | undefined =>
{
    for(const item of iterable) {
        if(p(item)) return item;
    }
}

export const uuid4 = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set the version bits to indicate version 4
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    // Set the variant bits
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const chrs = bytes.reduce(
        (a, b) => a + b.toString(16).padStart(2, '0'),
        '');

    return (
        chrs.slice( 0,  8) + '-' +
        chrs.slice( 8, 12) + '-' +
        chrs.slice(12, 16) + '-' +
        chrs.slice(16, 20) + '-' +
        chrs.slice(20, 32)
    );
};

/**
 * Randomly re-order the input array.
 */
export const shuffle = (array: unknown[]): void => {
    for(let i = array.length - 1; i > 0; i--)
    {
        const j = Math.floor(Math.random() * i);
        [array[j], array[i]] = [array[i], array[j]]; // swap
    }
}

type Resolver = () => void;

export const promiseWithResolvers = (): [Promise<void>, Resolver, Resolver] => {
    let
        resolve: Resolver,
        reject: Resolver;

    const p = new Promise<void>((resolver, rejecter) => {
        resolve = resolver;
        reject = rejecter;
    });

    return [p, resolve, reject];
}
